import { batch } from 'react-redux'

import { logError, logInfo, logInMeetingMessage } from '~/core/logger'

import {
  getConfig,
  getDetailsForJoinCallback,
  getInterfaceConfig,
  getIsSessionClosed,
  getMeetingTimerArgs,
  getRoomName,
  getStartSilent,
  getSubject
} from '~/selectors/active-session'

import { endMeeting as closeMeeting, initIframeApi, initIframeApiUndocked } from '~/services/jitsi-api'
import bridge from '~/services/bridge'
import {
  getAuthenticated,
  getClientId,
  getClientUpdateChannel,
  getShouldLaunchUndocked,
  getStoreLocale,
  getUserInfo
} from '~/selectors/app'
import { handleJoinFailed, resetActiveSession, setSessionStatus } from './active-session'
import { endCurrentCall } from '~/services/call-control'
import { ACTION_TYPES, BRIDGE_EVENTS, DOCK_IPC, FUNCTIONS, IFRAME_CONTAINER_ID, SESSION_STATUSES } from '~/core/constants'
import analytics from '~/services/analytics'
import { generateGUID } from '~/core/utils'

const { ACTIVE_SESSION } = ACTION_TYPES

const dispose = (iframeApi) => {
  if (iframeApi) {
    iframeApi.dispose()
  }
}

const handleLog = ({ logLevel, args }) => {
  logInMeetingMessage({ logLevel, args })
}

const handleSessionClosed = (iframeApi) => (dispatch, getState) => {
  const state = getState()

  // Guard against re-entry: a programmatic hangup (endMeeting) and the SDK's
  // own readyToClose event can both reach here. Once the session is CLOSED the
  // teardown is a no-op, otherwise onLeaveMeeting would fire twice.
  if (getIsSessionClosed(state)) {
    return
  }

  const roomName = getRoomName(state)

  analytics.trackInitEvent(analytics.EVENT_NAMES.LEAVE_MEETING, {
    meetingId: roomName
  })

  dispose(iframeApi)
  dispatch(resetActiveSession())
  endCurrentCall()

  callbacks.onLeaveMeeting()
}

const handleRoomJoined = (parentNode, iframeApi) => (dispatch, getState) => {
  const state = getState()

  logInfo({ __filename }, 'Received room joined event.')

  if (iframeApi) {
    const subject = getSubject(state)

    if (subject) {
      iframeApi.executeCommand('subject', subject)
    }

    // Drive the calendar-aware meeting-pace timer. Jitsi replaced the old
    // `configOverwrite.timeTimer` duration keys with this runtime command, so
    // we push the joined event's duration/elapsed once the room is joined.
    // Null when the participant joined an ad-hoc meeting (no calendar event).
    //
    // Guard on getSupportedCommands(): an older Jitsi deployment whose
    // external_api.js predates `setMeetingTimer` would otherwise log a
    // "Not supported command name." error and no-op. The check reads the same
    // command map executeCommand validates against, so on old Jitsi we skip
    // the call silently (meeting unaffected) and the timer lights up
    // automatically once the new Jitsi is deployed.
    const timerArgs = getMeetingTimerArgs(state)

    if (timerArgs && iframeApi.getSupportedCommands().includes('setMeetingTimer')) {
      iframeApi.executeCommand('setMeetingTimer', timerArgs)
    }
  }

  // in the rare case iframe load triggers after this event
  dispatch(setWaitingToJoin(false))

  if (parentNode) {
    parentNode.querySelector('iframe').focus()
  }

  dispatch(setSessionStatus(SESSION_STATUSES.CONNECTED))
}

const handleScreenSharingStatusChanged = event => {
  bridge.emit(BRIDGE_EVENTS.SCREEN_SHARING_STATUS_CHANGED, event)
}

export const setWaitingToJoin = value => ({
  type: ACTIVE_SESSION.SET_WAITING_TO_JOIN,
  payload: value
})

export const callbacks = {
  onJoinMeetingStarted: FUNCTIONS.NOOP,
  onLeaveMeeting: FUNCTIONS.NOOP
}

export const loadJitsiMeeting = () => async (dispatch, getState) => {
  const state = getState()

  const startSilent = getStartSilent(state)
  const authenticated = getAuthenticated(state)
  const { email, name: displayName } = getUserInfo(state)
  const configuration = getConfig(state)
  const interfaceConfiguration = getInterfaceConfig(state)
  const roomName = getRoomName(state)
  const locale = getStoreLocale(state)

  const parentNode = document.getElementById(IFRAME_CONTAINER_ID)

  const handleLeave = iframeApi => {
    logInfo({ __filename }, 'Handling room leave event.')

    iframeApi.getIFrame().src = 'about:blank'

    setTimeout(() => {
      dispatch(handleSessionClosed(iframeApi))
    }, 300)
  }

  if (!parentNode) {
    logError(
      { __filename },
      'Missing jitsi iframe container. Failed to join meeting'
    )
    return
  }

  batch(() => {
    dispatch(setSessionStatus(SESSION_STATUSES.CONNECTING))
    dispatch(setWaitingToJoin(true))
  })

  dispose()

  const iframeApi = await initIframeApi({
    authenticated,
    configuration,
    displayName: displayName || email,
    email,
    handlers: {
      log: handleLog,
      readyToClose: () => handleLeave(iframeApi),
      screenSharingStatusChanged: handleScreenSharingStatusChanged,
      suspendDetected: () => handleLeave(iframeApi),
      videoConferenceJoined: () => {
        dispatch(handleRoomJoined(parentNode, iframeApi))
        callbacks.onJoinMeetingStarted(startSilent, getDetailsForJoinCallback(state))
      }
    },
    interfaceConfiguration,
    onload: () => dispatch(setWaitingToJoin(false)),
    parentNode,
    roomName,
    locale
  })
}

export const loadUndockedJitsiMeeting = ({ preload = false } = { preload: false }) => async (dispatch, getState) => {
  const state = getState()
  const { customerId, name: displayName, email } = getUserInfo(state)
  const authenticated = getAuthenticated(state)
  const startSilent = getStartSilent(state)
  const locale = getStoreLocale(state)

  if (!preload) {
    callbacks.onJoinMeetingStarted(startSilent, getDetailsForJoinCallback(state))
  }

  await initIframeApiUndocked({
    authenticated,
    clientId: getClientId(state),
    clientUpdateChannel: getClientUpdateChannel(state),
    configuration: getConfig(state),
    customerId,
    displayName: displayName || email,
    email,
    handlers: {
      [DOCK_IPC.CLOSED]: () => dispatch(handleSessionClosed()),
      [DOCK_IPC.CRASHED_AND_CLOSED]: () => dispatch(handleSessionClosed()),
      [DOCK_IPC.JOIN_FAILED]: err => dispatch(handleJoinFailed(err)),
      [DOCK_IPC.LOADED]: () => {
        dispatch(setSessionStatus(SESSION_STATUSES.CONNECTING))
        callbacks.onJoinMeetingStarted(startSilent)
      },
      [DOCK_IPC.LOG]: handleLog,
      [DOCK_IPC.ROOM_JOINED]: () => dispatch(handleRoomJoined()),
      [DOCK_IPC.ROOM_LEFT]: () => dispatch(handleSessionClosed()),
      [DOCK_IPC.SCREENSHARING_STATUS_CHANGED]: handleScreenSharingStatusChanged
    },
    interfaceConfiguration: getInterfaceConfig(state),
    preload,
    roomName: preload ? generateGUID() : getRoomName(state),
    subject: getSubject(state),
    locale
  })
}

export const endMeeting = () => (dispatch, getState) => {
  const isUndocked = getShouldLaunchUndocked(getState())

  closeMeeting({ isUndocked })

  // A programmatic hangup does not reliably echo a readyToClose/CLOSED event
  // back from the Jitsi SDK (JIT-15871). Without it the session is stranded in
  // CONNECTING/CONNECTED, so the next join is misread as an active meeting and
  // opens the spurious "switch meeting" dialog. Drive the teardown ourselves;
  // handleSessionClosed is re-entrancy guarded, so a late SDK event is a no-op.
  setTimeout(() => {
    dispatch(handleSessionClosed())
  }, 300)
}
